import { db } from '../../firebase/firebaseConfig';
import { doc, updateDoc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';

/** Cap for cookbook cards / snippet leveling (XP curve runs to 100 mathematically; gameplay caps here). */
export const MAX_SNIPPET_LEVEL = 25;

/**
 * Apply EXP with level-ups using {@link LevelingSystem#calculateExpToLevel}. No-op when already at max level.
 * @param {LevelingSystem} levelingSystem - Instance with calculateExpToLevel
 * @param {number} level - Current level (values above max are treated as max)
 * @param {number} currentExp - Current EXP toward next level
 * @param {number|undefined|null} expToLevelMaybe - Threshold to complete current level bar; derived if missing
 * @param {number} expGain - EXP to add (e.g. 10)
 * @returns {{ level: number, current_exp: number, exp_to_level: number, leveledUp: boolean }}
 */
export function applySnippetExpGain(levelingSystem, level, currentExp, expToLevelMaybe, expGain) {
  let lvl = Math.min(Math.max(level || 1, 1), MAX_SNIPPET_LEVEL);
  let cur = currentExp || 0;
  let need =
    expToLevelMaybe == null
      ? levelingSystem.calculateExpToLevel(Math.min(lvl + 1, MAX_SNIPPET_LEVEL + 1))
      : expToLevelMaybe;

  if (lvl >= MAX_SNIPPET_LEVEL) {
    return {
      level: MAX_SNIPPET_LEVEL,
      current_exp: cur,
      exp_to_level: levelingSystem.calculateExpToLevel(MAX_SNIPPET_LEVEL + 1),
      leveledUp: false
    };
  }

  let leveledUp = false;
  cur += expGain;
  while (cur >= need && lvl < MAX_SNIPPET_LEVEL) {
    lvl++;
    cur -= need;
    leveledUp = true;
    if (lvl >= MAX_SNIPPET_LEVEL) {
      need = levelingSystem.calculateExpToLevel(MAX_SNIPPET_LEVEL + 1);
      break;
    }
    need = levelingSystem.calculateExpToLevel(lvl + 1);
  }
  return { level: lvl, current_exp: cur, exp_to_level: need, leveledUp };
}

class LevelingSystem {
  constructor(userId) {
    this.userId = userId;
  }

  // Calculate experience required to reach a specific level
  calculateExpToLevel(level) {
    return Math.round(100 * Math.pow(10, (level - 1) / (99 / Math.log(10))));
  }

  /**
   * Legacy standalone snippets (users/{uid}/snippets / SnippetManager) are no longer supported.
   * EXP for study sessions applies to cookbook cards and cookbooks only.
   */
  async addSnippetExp(_snippetId, _expAmount) {
    return null;
  }

  // Add experience to a cookbook and update its level based on snippet levels
  async updateCookbookLevel(cookbookId) {
    try {
      // Get the cookbook
      const cookbookRef = doc(db, `users/${this.userId}/decks/${cookbookId}`);
      const cookbookDoc = await getDoc(cookbookRef);

      if (!cookbookDoc.exists()) {
        console.error('Cookbook not found:', cookbookId);
        return null;
      }

      // Get all cards in the cookbook
      const cardsRef = collection(db, `users/${this.userId}/decks/${cookbookId}/cards`);
      const snapshot = await getDocs(cardsRef);

      if (snapshot.empty) {
        // No cards, set level to 1
        await updateDoc(cookbookRef, { level: 1 });
        return { level: 1 };
      }

      // Calculate average level of all cards
      let totalLevel = 0;
      let cardCount = 0;

      snapshot.forEach(doc => {
        const cardData = doc.data();
        if (cardData.level) {
          totalLevel += cardData.level;
          cardCount++;
        }
      });

      const averageLevel = cardCount > 0 ? Math.round(totalLevel / cardCount) : 1;

      // Update cookbook level
      await updateDoc(cookbookRef, { level: averageLevel });

      return { level: averageLevel };
    } catch (error) {
      console.error('Error updating cookbook level:', error);
      throw error;
    }
  }

  // Initialize leveling fields for a new snippet
  initializeSnippetLeveling() {
    return {
      level: 1,
      current_exp: 0,
      exp_to_level: this.calculateExpToLevel(2) // Experience needed to reach level 2
    };
  }

  // Initialize leveling fields for a new cookbook
  initializeCookbookLeveling() {
    return {
      level: 1
    };
  }
}

export default LevelingSystem;
